import React, { useEffect, useState } from "react";
import {
  collection,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Autocomplete from "@mui/material/Autocomplete";
import Alert from "@mui/material/Alert";
import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import { nanoid } from "nanoid";

import { firebaseAuth } from "../../components/firebase/auth";
import { firebaseStore } from "../../components/firebase/firestore";
import "./dashboard.css";
import "./batchWorkspace.css";
import "./freshPackages.css";

interface FreshParent {
  id: string;
  name_sv: string;
  name_en: string;
  icon: string;
  logo: string;
  url: string;
  subtitle_sv: string;
  color_sv: string;
  flavor_sv: string;
}

interface FreshPackage {
  firebaseId: string;
  id: string;
  parentId: string;
  size: number;
  formatedSize: string;
  isBox: boolean;
  active?: boolean;
  boundBagPackageId?: string;
  priceStore?: number;
  priceRestaurant?: number;
  priceRetail?: number;
  priceOther?: number;
  ean: string;
  storeData_sv: string;
  storeData_en: string;
  storeTemp: number;
  shelfLife: string;
  menuName_sv: string;
  menuName_en: string;
  createdAt: number;
}

function DashBoardFreshPackages() {
  const [user, loading] = useAuthState(firebaseAuth);
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<FreshPackage | null>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [packages, setPackages] = useState<FreshPackage[]>([]);
  const [firebaseLoading, setFirebaseLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [parentsLoading, setParentsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [saveError, setSaveError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<FreshPackage | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  // Sort state
  const [sortBy, setSortBy] = useState<"name" | "created" | "size">("created");

  // Parents from products_fresh
  const [parents, setParents] = useState<FreshParent[]>([]);
  const [selectedParent, setSelectedParent] = useState<FreshParent | null>(null);

  // Form fields
  const [size, setSize] = useState<number | null>(null);
  const [formatedSize, setFormatedSize] = useState("");
  const [isBox, setIsBox] = useState(false);
  const [active, setActive] = useState(true);
  const [boundBagPackage, setBoundBagPackage] = useState<FreshPackage | null>(null);
  const [priceStore, setPriceStore] = useState<number | null>(null);
  const [priceRestaurant, setPriceRestaurant] = useState<number | null>(null);
  const [priceRetail, setPriceRetail] = useState<number | null>(null);
  const [priceOther, setPriceOther] = useState<number | null>(null);
  const [ean, setEan] = useState("");
  const [storeDataSv, setStoreDataSv] = useState("");
  const [storeDataEn, setStoreDataEn] = useState("");
  const [storeTemp, setStoreTemp] = useState<number | null>(null);
  const [shelfLife, setShelfLife] = useState("");
  const [menuNameSv, setMenuNameSv] = useState("");
  const [menuNameEn, setMenuNameEn] = useState("");

  // Listen to products_fresh for parent selection
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh"), orderBy("name_sv"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const temp: FreshParent[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        temp.push({ id: doc.id, name_sv: data.name_sv || "", name_en: data.name_en || "", icon: data.icon || "", logo: data.logo || "", url: data.url || "", subtitle_sv: data.subtitle_sv || "", color_sv: data.color_sv || "", flavor_sv: data.flavor_sv || "" });
      });
      setParents(temp);
      setParentsLoading(false);
    }, () => setLoadError("Produkterna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  // Listen to packages
  useEffect(() => {
    const q = query(collection(firebaseStore, "products_fresh_package"), orderBy("createdAt", "desc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFirebaseLoading(true);
      const temp: FreshPackage[] = [];
      snapshot.forEach((doc) => { temp.push({ firebaseId: doc.id, ...doc.data() } as FreshPackage); });
      setPackages(temp);
      setFirebaseLoading(false);
    }, () => setLoadError("Förpackningarna kunde inte hämtas."));
    return () => unsubscribe();
  }, [reload]);

  useEffect(() => { if (user === null && loading === false) navigate("/login"); }, [user, loading, navigate]);

  const getParentName = (parentId: string): string => {
    const p = parents.find((x) => x.id === parentId);
    return p ? `${p.name_sv}${p.name_en ? ` / ${p.name_en}` : ""}` : parentId;
  };

  const isPackageActive = (pkg: FreshPackage): boolean => {
    if (pkg.active === false) return false;
    if (!pkg.boundBagPackageId) return true;
    return packages.find((candidate) => candidate.id === pkg.boundBagPackageId)?.active !== false;
  };

  const getBoundBoxesForBag = (bagPackage: FreshPackage): FreshPackage[] =>
    packages.filter((candidate) => candidate.isBox && candidate.boundBagPackageId === bagPackage.id);

  const getSortedPackages = (): FreshPackage[] => {
    const sorted = packages.filter((pkg) => {
      const text = `${getParentName(pkg.parentId)} ${pkg.formatedSize} ${pkg.size} ${pkg.ean} ${pkg.menuName_sv || ""} ${pkg.menuName_en || ""}`.toLocaleLowerCase("sv");
      return text.includes(search.trim().toLocaleLowerCase("sv")) && (statusFilter === "all" || isPackageActive(pkg) === (statusFilter === "active")) && (typeFilter === "all" || Boolean(pkg.isBox) === (typeFilter === "box"));
    });
    if (sortBy === "name") {
      sorted.sort((a, b) => {
        const aName = getParentName(a.parentId).toLowerCase();
        const bName = getParentName(b.parentId).toLowerCase();
        return aName.localeCompare(bName);
      });
    } else if (sortBy === "size") {
      sorted.sort((a, b) => (a.size || 0) - (b.size || 0));
    } else {
      sorted.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    }
    return sorted;
  };

  const getPackageParentGroups = (): Array<{ parentId: string; packages: FreshPackage[] }> => {
    const groups = new Map<string, FreshPackage[]>();
    getSortedPackages().forEach((pkg) => {
      const group = groups.get(pkg.parentId) || [];
      group.push(pkg);
      groups.set(pkg.parentId, group);
    });
    return Array.from(groups.entries()).map(([parentId, groupedPackages]) => {
      const pairedOrder: FreshPackage[] = [];
      const included = new Set<string>();

      groupedPackages.forEach((pkg) => {
        if (included.has(pkg.id) || pkg.isBox) return;
        pairedOrder.push(pkg);
        included.add(pkg.id);
        groupedPackages
          .filter((candidate) => candidate.isBox && candidate.boundBagPackageId === pkg.id)
          .forEach((box) => {
            pairedOrder.push(box);
            included.add(box.id);
          });
      });

      groupedPackages.forEach((pkg) => {
        if (!included.has(pkg.id)) pairedOrder.push(pkg);
      });

      return { parentId, packages: pairedOrder };
    });
  };

  const resetForm = () => {
    setSaveError("");
    setSelectedParent(null);
    setSize(null);
    setFormatedSize("");
    setIsBox(false);
    setActive(true);
    setBoundBagPackage(null);
    setPriceStore(null);
    setPriceRestaurant(null);
    setPriceRetail(null);
    setPriceOther(null);
    setEan("");
    setStoreDataSv("");
    setStoreDataEn("");
    setStoreTemp(null);
    setShelfLife("");
    setMenuNameSv("");
    setMenuNameEn("");
    setEditingPackage(null);
  };

  const handleOpenAdd = () => { resetForm(); setOpen(true); };

  const handleOpenEdit = (pkg: FreshPackage) => {
    setSaveError("");
    setEditingPackage(pkg);
    const parent = parents.find((p) => p.id === pkg.parentId);
    setSelectedParent(parent || null);
    setSize(pkg.size);
    setFormatedSize(pkg.formatedSize || "");
    setIsBox(pkg.isBox || false);
    setActive(pkg.active !== false);
    setBoundBagPackage(packages.find((candidate) => candidate.id === pkg.boundBagPackageId) || null);
    setPriceStore(typeof pkg.priceStore === "number" ? pkg.priceStore : null);
    setPriceRestaurant(typeof pkg.priceRestaurant === "number" ? pkg.priceRestaurant : null);
    setPriceRetail(typeof pkg.priceRetail === "number" ? pkg.priceRetail : null);
    setPriceOther(typeof pkg.priceOther === "number" ? pkg.priceOther : null);
    setEan(pkg.ean || "");
    setStoreDataSv(pkg.storeData_sv || "");
    setStoreDataEn(pkg.storeData_en || "");
    setStoreTemp(pkg.storeTemp);
    setShelfLife(pkg.shelfLife || "");
    setMenuNameSv(pkg.menuName_sv || "");
    setMenuNameEn(pkg.menuName_en || "");
    setOpen(true);
  };

  const handleClose = () => { if (!dialogLoading) setOpen(false); };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true); setDeleteError("");
    try {
      await deleteDoc(doc(firebaseStore, "products_fresh_package", deleteTarget.firebaseId));
      setDeleteTarget(null);
    } catch (error) { console.error("Error deleting package:", error); setDeleteError("Förpackningen kunde inte tas bort. Försök igen."); }
    finally { setDeleteLoading(false); }
  };
  const validSize = size !== null && Number.isFinite(size) && size > 0;
  const validPrices = isBox || [priceStore, priceRestaurant, priceRetail, priceOther].every((price) => price === null || (Number.isFinite(price) && price >= 0));
  const validTemperature = storeTemp === null || Number.isFinite(storeTemp);
  const validBag = !isBox || (boundBagPackage && !boundBagPackage.isBox && boundBagPackage.parentId === selectedParent?.id && boundBagPackage.id !== editingPackage?.id);
  const canSave = !!selectedParent && validSize && validPrices && validTemperature && !!validBag;
  const packageLabel = (pkg: FreshPackage) => pkg.formatedSize || `${pkg.size} g`;
  const formSection = (title: string, content: React.ReactNode) => <fieldset className="BatchFormSection PackageFormSection"><legend>{title}</legend>{content}</fieldset>;
  const dialogTitle = (id: string, title: string, subtitle: string, close: () => void, busy = false) => <DialogTitle component="div"><div><h2 id={id}>{title}</h2><p className="BatchDialogSubtitle">{subtitle}</p></div><IconButton aria-label="Stäng" disabled={busy} onClick={close}><CloseIcon /></IconButton></DialogTitle>;
  const numberField = (label: string, value: number | null, setter: (value: number | null) => void, price = false) => <TextField fullWidth size="small" label={label} type="number" value={value ?? ""} onChange={(event) => setter(event.target.value === "" ? null : Number(event.target.value))} disabled={dialogLoading} inputProps={{ step: "any", ...(price ? { min: 0 } : {}) }} error={price && value !== null && (!Number.isFinite(value) || value < 0)} helperText={price && value !== null && value < 0 ? "Priset får inte vara negativt." : undefined} />;
  const textField = (label: string, value: string, setter: (value: string) => void, placeholder?: string) => <TextField fullWidth size="small" label={label} value={value} onChange={(event) => setter(event.target.value)} disabled={dialogLoading} placeholder={placeholder} />;

  const handleSave = async () => {
    if (!canSave || !selectedParent || size === null) { setSaveError("Välj produkt, ange en positiv vikt och kontrollera priserna. Boxar behöver en kopplad påse från samma produkt."); return; }
    setSaveError("");
    setDialogLoading(true);
    const pkgId = editingPackage?.id || nanoid(8);

    const packageData = {
      id: pkgId,
      parentId: selectedParent.id,
      size,
      formatedSize: formatedSize.trim(),
      isBox,
      active: isBox && boundBagPackage ? boundBagPackage.active !== false : active,
      boundBagPackageId: isBox ? boundBagPackage?.id || "" : "",
      priceStore: isBox ? 0 : priceStore || 0,
      priceRestaurant: isBox ? 0 : priceRestaurant || 0,
      priceRetail: isBox ? 0 : priceRetail || 0,
      priceOther: isBox ? 0 : priceOther || 0,
      ean: ean.trim(),
      storeData_sv: storeDataSv.trim(),
      storeData_en: storeDataEn.trim(),
      storeTemp: storeTemp || 0,
      shelfLife: shelfLife.trim(),
      menuName_sv: menuNameSv.trim(),
      menuName_en: menuNameEn.trim(),
    };

    try {
      if (editingPackage) {
        await updateDoc(doc(firebaseStore, "products_fresh_package", editingPackage.firebaseId), packageData);
      } else {
        await addDoc(collection(firebaseStore, "products_fresh_package"), { ...packageData, createdAt: Date.now() });
      }
      setOpen(false);
    } catch (error) { console.error("Error saving package:", error); setSaveError("Förpackningen kunde inte sparas. Dina uppgifter finns kvar. Försök igen."); }
    setDialogLoading(false);
  };

  return (
    <div className="DashBoardTabInner FreshDashboardTab BatchWorkspace PackageWorkspace">
      <div className="FreshTabToolbar">
        <div>
          <p className="FreshEyebrow">Förpackning</p>
          <h2 className="FreshTabTitle">Färska förpackningar</h2>
          <p className="FreshTabSummary">{packages.length} {packages.length === 1 ? "förpackning" : "förpackningar"} i registret</p>
        </div>
        <div className="FreshTabControls">
          <IconButton className="FreshTabInfoButton" title="Om Färska förpackningar" aria-label="Om Färska förpackningar" onClick={() => setInfoDialogOpen(true)}><InfoOutlinedIcon /></IconButton>
          <Button color="inherit" variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
            Ny förpackning
          </Button>
        </div>
      </div>
      {loadError ? <Alert severity="error" action={<Button color="inherit" onClick={() => { setLoadError(""); setReload((value) => value + 1); }}>Försök igen</Button>}>{loadError}</Alert> : firebaseLoading || parentsLoading ? <div className="BatchEmpty" role="status"><CircularProgress size={24} color="inherit" /><p>Hämtar produkter och förpackningar…</p></div> : <>
        <div className="PackageOverview"><div><span>Påsar</span><strong>{packages.filter((pkg) => !pkg.isBox).length}</strong></div><div><span>Boxar</span><strong>{packages.filter((pkg) => pkg.isBox).length}</strong></div><div><span>Aktiva förpackningar</span><strong>{packages.filter(isPackageActive).length} <small>av {packages.length}</small></strong></div></div>
        <div className="PackageTools" role="search" aria-label="Filtrera förpackningar"><TextField className="PackageSearch" fullWidth size="small" label="Sök produkt, storlek eller EAN" value={search} onChange={(event) => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} /><TextField select size="small" label="Typ" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}><MenuItem value="all">Alla typer</MenuItem><MenuItem value="bag">Påsar</MenuItem><MenuItem value="box">Boxar</MenuItem></TextField><TextField select size="small" label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><MenuItem value="all">Alla statusar</MenuItem><MenuItem value="active">Aktiva</MenuItem><MenuItem value="inactive">Inaktiva</MenuItem></TextField><TextField select size="small" label="Sortera grupper" value={sortBy} onChange={(event) => setSortBy(event.target.value as "name" | "created" | "size")}><MenuItem value="created">Senast skapad</MenuItem><MenuItem value="name">Produktnamn</MenuItem><MenuItem value="size">Minsta vikt</MenuItem></TextField></div>
        <div className="PackageResult"><span>{getSortedPackages().length} av {packages.length} förpackningar · Påsar och kopplade boxar visas tillsammans.</span>{(search || typeFilter !== "all" || statusFilter !== "all") && <Button color="inherit" size="small" onClick={() => { setSearch(""); setTypeFilter("all"); setStatusFilter("all"); }}>Rensa filter</Button>}</div>
        <div className="FreshPackageProductGroups">
          {getSortedPackages().length === 0 ? (<div className="BatchEmpty"><Inventory2OutlinedIcon /><strong>{packages.length ? "Inga förpackningar matchar filtren" : "Skapa din första förpackning"}</strong><p>{packages.length ? "Prova ett annat sökord eller rensa filtren." : "Börja med en påse och koppla den till en färsk produkt."}</p>{!packages.length && <Button color="inherit" variant="outlined" onClick={handleOpenAdd}>Ny förpackning</Button>}</div>) : (
            getPackageParentGroups().map(({ parentId, packages: parentPackages }) => (
              <section className="FreshPackageProductGroup" key={parentId}>
                <div className="FreshPackageProductHeader">
                  <strong>{getParentName(parentId)}</strong>
                  <span>{parentPackages.length} {parentPackages.length === 1 ? "förpackning" : "förpackningar"}</span>
                </div>
                <div className="FreshPackageCards">
                  {parentPackages.map((pkg) => {
                    const parent = parents.find((p) => p.id === pkg.parentId);
                    const controllingBag = pkg.boundBagPackageId ? packages.find((candidate) => candidate.id === pkg.boundBagPackageId) : undefined;
                    const boundBoxes = !pkg.isBox ? getBoundBoxesForBag(pkg) : [];
                    const isPaired = Boolean(controllingBag || boundBoxes.length > 0);
                    return (
                      <div key={pkg.firebaseId} className={`PackageCard${pkg.isBox ? " isBox" : " isBag"}${isPaired ? " isPaired" : ""}${!isPackageActive(pkg) ? " isInactive" : ""}`}>
                        <div className="PackageCardMain">
                          <div style={{ width: 60, height: 60, margin: "0 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {parent?.icon ? (<img src={parent.icon} alt={parent.name_sv} style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} />) : parent?.logo ? (<img src={parent.logo} alt={parent.name_sv} style={{ width: 50, height: 50, objectFit: "contain", borderRadius: 4 }} />) : parent?.url ? (<img src={parent.url} alt={parent.name_sv} style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4 }} />) : (<div style={{ width: 50, height: 50, backgroundColor: "#eee", borderRadius: 4 }} />)}
                          </div>
                          <div className="PackageCardDetails">
                            <p className="PackageCardTitle"><strong>{pkg.formatedSize || `${pkg.size}g`}</strong><span className="FreshPackageTypeBadge">{pkg.isBox ? "Box" : "Påse"}</span>{isPaired ? <span className="FreshPackagePairBadge">Parad</span> : null}{!isPackageActive(pkg) ? <span className="FreshPackageInactiveBadge">Inaktiv</span> : null}</p>
                            <p className="PackageCardText">
                              {pkg.menuName_sv || parent?.subtitle_sv || ""}{pkg.storeData_sv ? ` | ${pkg.storeData_sv}` : ""}{pkg.shelfLife ? ` | Hållbarhet: ${pkg.shelfLife} dagar` : ""}
                            </p>
                            <p className="PackageCardText">
                              {pkg.ean ? `EAN: ${pkg.ean}` : "Ingen EAN"}
                            </p>
                            {controllingBag ? <p className="FreshPackagePairInfo">Styrs av påse {controllingBag.formatedSize || `${controllingBag.size}g`}</p> : null}
                            {boundBoxes.length > 0 ? <p className="FreshPackagePairInfo">Kopplade boxar: {boundBoxes.map((box) => box.formatedSize || `${box.size}g`).join(", ")}</p> : null}
                            {!pkg.isBox && <dl className="PackagePrices">{[["Butik", pkg.priceStore], ["Restaurang", pkg.priceRestaurant], ["Återförsäljare", pkg.priceRetail], ["Övrig", pkg.priceOther]].map(([label, price]) => <div key={String(label)}><dt>{label}</dt><dd>{Number(price || 0).toLocaleString("sv-SE")} kr</dd></div>)}</dl>}
                          </div>
                        </div>
                        <div className="PackageCardActions">
                          <IconButton color="inherit" title="Redigera förpackning" aria-label={`Redigera ${getParentName(pkg.parentId)}, ${packageLabel(pkg)}`} onClick={() => handleOpenEdit(pkg)}><EditIcon color="inherit" /></IconButton>
                          <IconButton color="inherit" title="Ta bort förpackning" aria-label={`Ta bort ${getParentName(pkg.parentId)}, ${packageLabel(pkg)}`} onClick={() => { setDeleteTarget(pkg); setDeleteError(""); }}><DeleteIcon color="inherit" /></IconButton>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>
      </>}
      {/* Info dialog */}
      <Dialog className="FreshDialog BatchDialog PackageDialog" fullWidth maxWidth="sm" aria-labelledby="package-info-title" open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
        {dialogTitle("package-info-title", "Om Färska förpackningar", "Förpackningar, priser och kopplingar mellan påsar och boxar.", () => setInfoDialogOpen(false))}
        <DialogContent>
          <div className="PackageInfo">
            <p className="FreshFlowIntro">Definiera hur en färsk produkt packas och säljs. Här skapar du påsar och boxar och kopplar dem till rätt produkt.</p>
            <ol className="FreshInfoSteps">
              <li>Klicka på <strong>Ny förpackning</strong> för att skapa en förpackning och välj rätt <strong>Produkt</strong>.</li>
              <li>Ange <strong>Storlek</strong> (t.ex. 40g) och markera om det är en <strong>Påse</strong> eller <strong>Box</strong>.</li>
              <li>Påsförpackningar används i kundfördelningen. En <strong>Box</strong> ska kopplas till en påse och följer då påsens aktiva/inaktiva status.</li>
              <li>Fyll i priser per paket, EAN, lagringstemperatur och hållbarhet för påsar.</li>
              <li>Den huvudsakliga mängdhanteringen sker i <strong>Färskt Lager</strong>.</li>
            </ol>
          </div>
        </DialogContent>
        <DialogActions><Button onClick={() => setInfoDialogOpen(false)} color="inherit" variant="contained">Stäng</Button></DialogActions>
      </Dialog>

      <Dialog className="FreshDialog BatchDialog PackageDialog" fullWidth maxWidth="md" open={open} onClose={handleClose} aria-labelledby="package-form-title">
        {dialogTitle("package-form-title", editingPackage ? `Redigera ${packageLabel(editingPackage)}` : "Ny förpackning", "Välj produkt och typ. Fyll sedan i vikt, priser och förvaringsuppgifter.", handleClose, dialogLoading)}
        <DialogContent><div className="PackageForm">
          {formSection("Produkt & förpackning", <>
            <Autocomplete options={parents} value={selectedParent} isOptionEqualToValue={(a, b) => a.id === b.id} getOptionLabel={(option) => `${option.name_sv}${option.name_en ? ` / ${option.name_en}` : ""}`} onChange={(_, value) => { setSelectedParent(value); setBoundBagPackage(null); }} disabled={dialogLoading} renderInput={(params) => <TextField {...params} size="small" label="Produkt" required />} />
            <div className="PackageFormGrid"><TextField select size="small" label="Förpackningstyp" value={isBox ? "box" : "bag"} onChange={(event) => setIsBox(event.target.value === "box")} disabled={dialogLoading}><MenuItem value="bag">Påse</MenuItem><MenuItem value="box">Box</MenuItem></TextField><TextField label="Vikt (g)" required type="number" size="small" value={size ?? ""} onChange={(event) => setSize(event.target.value === "" ? null : Number(event.target.value))} disabled={dialogLoading} inputProps={{ min: 0, step: "any" }} error={size !== null && !validSize} helperText={size !== null && !validSize ? "Ange en vikt större än noll." : isBox ? "Total vikt för hela boxen." : "Vikt per påse."} /></div>
            {textField("Storlek som visas", formatedSize, setFormatedSize, isBox ? "6 x 50 g" : "50 g")}
            {isBox && <Autocomplete options={packages.filter((pkg) => pkg.parentId === selectedParent?.id && !pkg.isBox && pkg.id !== editingPackage?.id)} value={boundBagPackage} isOptionEqualToValue={(a,b) => a.id === b.id} getOptionLabel={packageLabel} onChange={(_, value) => setBoundBagPackage(value)} disabled={dialogLoading || !selectedParent} noOptionsText="Inga påsar för vald produkt" renderInput={(params) => <TextField {...params} size="small" label="Kopplad påse" required helperText="Boxen följer den kopplade påsens aktiva eller inaktiva status." />} />}
            <FormControlLabel control={<Switch checked={isBox && boundBagPackage ? boundBagPackage.active !== false : active} onChange={(event) => setActive(event.target.checked)} disabled={dialogLoading || isBox} />} label={isBox ? "Status styrs av kopplad påse" : active ? "Aktiv för lager och beställningar" : "Inaktiv"} />
            {editingPackage && !isBox && getBoundBoxesForBag(editingPackage).length > 0 && <p className="PackageHint">Statusändringar påverkar även {getBoundBoxesForBag(editingPackage).length} kopplade boxar.</p>}
          </>)}
          {!isBox && formSection("Priser · SEK per paket", <div className="PackageFormGrid">{numberField("Butik",priceStore,setPriceStore,true)}{numberField("Restaurang",priceRestaurant,setPriceRestaurant,true)}{numberField("Återförsäljare",priceRetail,setPriceRetail,true)}{numberField("Övrig",priceOther,setPriceOther,true)}</div>)}
          {formSection("Märkning & meny", <>{textField("EAN",ean,setEan)}<div className="PackageFormGrid">{textField("Menynamn · svenska",menuNameSv,setMenuNameSv)}{textField("Menynamn · engelska",menuNameEn,setMenuNameEn)}</div></>)}
          {formSection("Förvaring & hållbarhet", <><div className="PackageFormGrid">{textField("Förvaring · svenska",storeDataSv,setStoreDataSv,"Kylvara")}{textField("Förvaring · engelska",storeDataEn,setStoreDataEn,"Keep refrigerated")}{numberField("Förvaringstemperatur (°C)",storeTemp,setStoreTemp)}{textField("Hållbarhet (dagar)",shelfLife,setShelfLife,"7–10")}</div></>)}
        </div></DialogContent>
        {saveError && <Alert severity="error" className="PackageDialogError">{saveError}</Alert>}
        <DialogActions><Button onClick={handleClose} color="inherit" disabled={dialogLoading}>Avbryt</Button><Button onClick={handleSave} color="inherit" variant="contained" disabled={dialogLoading || !canSave}>{dialogLoading ? "Sparar…" : editingPackage ? "Spara ändringar" : "Skapa förpackning"}</Button></DialogActions>
      </Dialog>
      <Dialog className="FreshDialog BatchDialog PackageDialog" fullWidth maxWidth="sm" open={!!deleteTarget} onClose={deleteLoading ? undefined : () => setDeleteTarget(null)} aria-labelledby="package-delete-title">
        {dialogTitle("package-delete-title", "Ta bort förpackning?", "Förpackningen tas bort permanent från registret.", () => setDeleteTarget(null), deleteLoading)}
        <DialogContent>{deleteTarget && <div className="PackageDeletePreview"><strong>{getParentName(deleteTarget.parentId)}</strong><span>{packageLabel(deleteTarget)} · {deleteTarget.isBox ? "Box" : "Påse"}</span></div>}<p className="PackageHint">Om förpackningen används i lager eller beställningar kan du i stället göra den inaktiv via Redigera.</p>{deleteTarget && getBoundBoxesForBag(deleteTarget).length > 0 && <Alert severity="warning">{getBoundBoxesForBag(deleteTarget).length} boxar är kopplade till påsen. Koppla om boxarna innan du tar bort den.</Alert>}</DialogContent>
        {deleteError && <Alert severity="error" className="PackageDialogError">{deleteError}</Alert>}
        <DialogActions><Button color="inherit" disabled={deleteLoading} onClick={() => setDeleteTarget(null)}>Avbryt</Button><Button color="inherit" variant="contained" className="PackageDeleteButton" disabled={deleteLoading || !!(deleteTarget && getBoundBoxesForBag(deleteTarget).length)} onClick={handleDelete}>{deleteLoading ? "Tar bort…" : "Ta bort förpackning"}</Button></DialogActions>
      </Dialog>
    </div>
  );
}

export default DashBoardFreshPackages;
